// [PURPOSE]: Pre-party splash inside the Party tab. Shows two cards
//   (Create / Join) when partyId === null. State machine: Idle → Busy →
//   PostCreate → unmount.
// [WHY]: Spec 2026-05-11-party-create-join. Settings was the wrong home —
//   "where do I get into a party?" now answers itself.
// [INTEGRATION]: Mounted by App.jsx Party tab dispatcher (Task 11) when
//   partyId is null. Calls createPartyHandler / joinPartyHandler from
//   PartyContext (Task 4) via props.

import React, { useState, useCallback } from 'react';
import { Crown, Link2 } from 'lucide-react';
import { InviteShareCopyRow } from './InviteShareCopyRow';

export function PartyCreateJoinSplash({
  currentUser,
  playerStats,
  currentPartyId,
  currentPartyName,
  onCreated,
  onJoined,
  createPartyHandler,
  joinPartyHandler,
}) {
  // [HOOKS ORDER FIX]: All hooks MUST be declared before any conditional return.
  //   The auth-banner early return was originally placed above the useState calls;
  //   that violated the Rules of Hooks if currentUser?.uid flipped truthy↔falsy
  //   across renders. Hooks first, conditional returns after.
  const [view, setView] = useState('idle');           // 'idle' | 'busy' | 'post-create'
  const [createName, setCreateName] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [customId, setCustomId] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [joinCode, setJoinCode] = useState('');
  const [joinPassword, setJoinPassword] = useState('');
  const [joinError, setJoinError] = useState(null);

  const [createdPartyName, setCreatedPartyName] = useState(null);
  const [createdPartyId, setCreatedPartyId] = useState(null);
  const [createdPassword, setCreatedPassword] = useState(null);

  const [pendingJoin, setPendingJoin] = useState(null);

  // [SPEC]: Invite code must be 6-32 chars, [A-Za-z0-9_-]. Client-side gate before
  //   reaching joinPartyHandler so users get fast feedback for malformed codes.
  const INVITE_CODE_PATTERN = /^[A-Za-z0-9_-]+$/;

  const handleCreate = useCallback(async () => {
    if (view === 'busy') return;
    setCreateError(null);
    if (!createName.trim()) {
      setCreateError('Pick a party name');
      return;
    }
    setView('busy');
    const result = await createPartyHandler({
      partyName: createName.trim(),
      customPartyId: customId,
      password: createPassword,
      characterId: playerStats?.id,
      characterName: playerStats?.name,
      hitPoints: playerStats?.hitPoints,
      maxHitPoints: playerStats?.maxHitPoints,
      authUid: currentUser?.uid,
    });
    if (!result.ok) {
      setCreateError(result.error);
      setView('idle');
      return;
    }
    setCreatedPartyId(result.partyId);
    setCreatedPartyName(result.partyName);
    setCreatedPassword(createPassword.trim() || null);
    setView('post-create');
  }, [view, createName, customId, createPassword, playerStats, currentUser, createPartyHandler]);

  const handleJoin = useCallback(async () => {
    if (view === 'busy') return;
    setJoinError(null);
    const trimmedCode = joinCode.trim();
    if (!trimmedCode) {
      setJoinError('Enter an invite code');
      return;
    }
    if (trimmedCode.length < 6 || trimmedCode.length > 32 || !INVITE_CODE_PATTERN.test(trimmedCode)) {
      setJoinError('Invite code must be 6-32 chars: letters, numbers, underscore, hyphen');
      return;
    }
    if (!playerStats?.name) {
      setJoinError('Give your character a name first');
      return;
    }

    // [SPEC]: If user is already in a party, surface a confirmation prompt
    //   before switching. Cancel returns to idle; "Leave & Join" sets
    //   pendingJoin to null and re-calls handleJoin (which now bypasses
    //   this check because pendingJoin was set last time it ran).
    if (currentPartyId && !pendingJoin) {
      setPendingJoin({ inviteCode: trimmedCode, password: joinPassword });
      return;
    }
    setPendingJoin(null);

    setView('busy');
    const result = await joinPartyHandler({
      inviteCode: trimmedCode,
      password: joinPassword,
      characterId: playerStats.id,
      characterName: playerStats.name,
      hitPoints: playerStats.hitPoints,
      maxHitPoints: playerStats.maxHitPoints,
      authUid: currentUser?.uid,
    });
    if (!result.ok) {
      setJoinError(result.error);
      setView('idle');
      return;
    }
    onJoined?.({ partyId: result.partyId, partyName: result.partyName });
  }, [view, joinCode, joinPassword, playerStats, currentUser, joinPartyHandler, onJoined, currentPartyId, pendingJoin]);

  const handleOpenDashboard = useCallback(() => {
    onCreated?.({ partyId: createdPartyId, partyName: createdPartyName });
  }, [onCreated, createdPartyId, createdPartyName]);

  // [UF-038 PRESERVED from commit #124]: Sign-in banner shown after all hooks are
  //   declared (Rules of Hooks compliance). The banner replaces the form when the
  //   user isn't authed; useState/useCallback above are inert in that branch but
  //   their declarations preserve stable hook order across renders.
  if (!currentUser?.uid) {
    return (
      <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4 text-center">
        <p className="text-amber-300 font-semibold mb-2">⚠️ Sign in required</p>
        <p className="text-sm text-gray-300">
          Party sync needs an account so your party stays connected across devices.
          Sign in (top-right) and come back to create or join a party.
        </p>
      </div>
    );
  }

  if (pendingJoin && currentPartyId) {
    return (
      <div className="max-w-md mx-auto bg-amber-900/30 border border-amber-700 rounded-lg p-5 text-center">
        <p className="text-amber-200 font-semibold mb-3">
          {`Leave party ${currentPartyName || currentPartyId} and join the new one?`}
        </p>
        <div className="flex gap-3 justify-center">
          <button
            type="button"
            onClick={handleJoin}
            className="px-4 py-2 bg-blue-700 hover:bg-blue-600 text-white font-semibold rounded-md"
          >
            Leave & Join
          </button>
          <button
            type="button"
            onClick={() => setPendingJoin(null)}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-gray-100 rounded-md"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  if (view === 'post-create') {
    return (
      <div className="max-w-md mx-auto bg-gray-900 border border-gray-700 rounded-lg p-6">
        <h2 className="text-xl font-bold text-green-300 mb-4">
          ✅ Party '{createdPartyName}' created!
        </h2>
        <p className="text-sm text-gray-400 mb-3">Share with your players:</p>
        <div className="space-y-3 mb-6">
          <InviteShareCopyRow label="Invite code" value={createdPartyId} />
          {createdPassword && (
            <InviteShareCopyRow label="Password" value={createdPassword} />
          )}
        </div>
        <button
          type="button"
          onClick={handleOpenDashboard}
          className="w-full px-4 py-3 bg-purple-700 hover:bg-purple-600 text-white font-semibold rounded-lg"
        >
          Open party dashboard →
        </button>
      </div>
    );
  }

  const busy = view === 'busy';

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto">
      {/* Create card */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
        <h2 className="text-lg font-bold text-purple-300 flex items-center gap-2 mb-4">
          <Crown size={20} /> Create a party
        </h2>
        {createError && (
          <div role="alert" className="bg-red-900/40 border border-red-700 rounded-md p-2 mb-3 text-sm text-red-200">
            {createError}
          </div>
        )}
        <label className="block mb-3">
          <span className="text-sm text-gray-300 mb-1 block">Party name</span>
          <input
            type="text"
            value={createName}
            onChange={(e) => setCreateName(e.target.value)}
            maxLength={64}
            disabled={busy}
            aria-label="Party name"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
            placeholder="Tuesday Crew"
          />
        </label>
        <label className="block mb-3">
          <span className="text-sm text-gray-300 mb-1 block">Password (optional)</span>
          <input
            type="password"
            value={createPassword}
            onChange={(e) => setCreatePassword(e.target.value)}
            maxLength={64}
            disabled={busy}
            autoComplete="new-password"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
            placeholder=""
          />
          <span className="text-xs text-gray-500 mt-1 block">
            Members will need this to join — share it with your players. (The cleartext is shown on the share screen after Create.)
          </span>
        </label>
        <button
          type="button"
          onClick={() => setAdvancedOpen(o => !o)}
          className="text-xs text-purple-400 hover:text-purple-300 mb-2"
        >
          {advancedOpen ? 'Advanced ▴' : 'Advanced ▾'}
        </button>
        {advancedOpen && (
          <label className="block mb-3">
            <span className="text-sm text-gray-300 mb-1 block">Custom Party ID</span>
            <input
              type="text"
              value={customId}
              onChange={(e) => setCustomId(e.target.value)}
              maxLength={32}
              disabled={busy}
              className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white font-mono text-sm"
              placeholder="tuesday-crew"
            />
            <span className="text-xs text-gray-500 mt-1 block">
              A memorable ID lets your group reuse the same party across sessions.
            </span>
          </label>
        )}
        <button
          type="button"
          onClick={handleCreate}
          disabled={busy || !createName.trim()}
          className="w-full px-4 py-2 bg-purple-700 hover:bg-purple-600 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded-md"
        >
          {busy ? 'Creating…' : 'Create'}
        </button>
      </div>

      {/* Join card */}
      <div className="bg-gray-900 border border-gray-700 rounded-lg p-5">
        <h2 className="text-lg font-bold text-blue-300 flex items-center gap-2 mb-4">
          <Link2 size={20} /> Join a party
        </h2>
        {joinError && (
          <div role="alert" className="bg-red-900/40 border border-red-700 rounded-md p-2 mb-3 text-sm text-red-200">
            {joinError}
          </div>
        )}
        <label className="block mb-3">
          <span className="text-sm text-gray-300 mb-1 block">Invite code (from your DM)</span>
          <input
            type="text"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value)}
            maxLength={32}
            disabled={busy}
            aria-label="Invite code"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white font-mono text-sm"
            placeholder="Tuesday-Crew"
          />
        </label>
        <label className="block mb-3">
          <span className="text-sm text-gray-300 mb-1 block">Password (if required)</span>
          <input
            type="password"
            value={joinPassword}
            onChange={(e) => setJoinPassword(e.target.value)}
            maxLength={64}
            disabled={busy}
            autoComplete="current-password"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white"
            placeholder=""
          />
        </label>
        <button
          type="button"
          onClick={handleJoin}
          disabled={busy || !joinCode.trim()}
          className="w-full px-4 py-2 bg-blue-700 hover:bg-blue-600 disabled:bg-gray-700 disabled:text-gray-500 text-white font-semibold rounded-md"
        >
          {busy ? 'Joining…' : 'Join'}
        </button>
      </div>
    </div>
  );
}
